import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Grid,
  Typography,
  Paper,
  Button,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import { Save, Add, Delete, CheckCircle, CloudUpload, Image as ImageIcon } from "@mui/icons-material";
import { Missionary, MissionDetails, CompanionDetails } from "../Mission/MissionaryArtTab";
import { doc, getDoc, updateDoc, serverTimestamp, setDoc, arrayUnion } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, useAuth, storage } from "../../../firebase";
import { checkAndUpdateOrderProgress } from "../../../utils/firebaseUtils";
import { useNavigate } from "react-router-dom";

interface MissionaryEditorProps {
  missionaryId: string;
  orderId?: string;
  isLocked?: boolean;
  onSave?: () => void;
}

interface UploadedFile {
  name: string;
  url: string;
  size?: number;
  uploadedAt?: string;
}

// Media Picker Dialog
const MediaLibraryPicker: React.FC<{
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}> = ({ open, onClose, onSelect }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
       setLoading(true);
       getDoc(doc(db, "users", user.uid)).then(snap => {
           if (snap.exists()) {
               setFiles(snap.data().mediaLibrary || []);
           }
           setLoading(false);
       });
    }
  }, [open, user]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Select from Media Library</DialogTitle>
        <DialogContent>
            {loading ? <CircularProgress /> : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {files.length === 0 && <Typography>No files found in library.</Typography>}
                    {files.map((file, i) => (
                        <Grid size={{ xs: 4, sm: 3 }} key={i}>
                            <Box 
                                onClick={() => onSelect(file.url)}
                                sx={{ 
                                    border: '1px solid #ddd', 
                                    borderRadius: 1, 
                                    cursor: 'pointer', 
                                    overflow: 'hidden',
                                    height: 120,
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,0,0,0.02)' }
                                }}
                            >
                                {file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) || file.url.includes('firebasestorage') ? (
                                    <img src={file.url} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <Box sx={{ p: 2, textAlign: 'center' }}><ImageIcon fontSize="large" /></Box>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
    </Dialog>
  );
};


// Inline File Upload Component
const FileUploadButton: React.FC<{
  currentUrl?: string;
  onUpload: (url: string, file: File) => void;
  storagePath: string;
  label: string;
  disabled?: boolean;
  onSelectFromLibrary?: () => void;
}> = ({ currentUrl, onUpload, storagePath, label, disabled, onSelectFromLibrary }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    setUploading(true);
    try {
      const storageRef = ref(storage, `${storagePath}/${Date.now()}_${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      
      task.on('state_changed', 
        () => {}, // progress
        (err) => { console.error(err); setUploading(false); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          onUpload(url, file);
          setUploading(false);
        }
      );
    } catch (err) {
      console.error(err);
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>{label}</Typography>
      
      {currentUrl ? (
         <Box sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
            <img src={currentUrl} alt="Upload" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {!disabled && (
              <IconButton 
                size="small" 
                sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.8)' }}
                onClick={() => onUpload("", new File([], ""))}
              >
                <Delete fontSize="small" color="error" />
              </IconButton>
            )}
         </Box>
      ) : (
         <Box sx={{ display: 'flex', gap: 1 }}>
             <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                disabled={uploading || disabled}
             >
                {uploading ? "Uploading..." : "Upload"}
                <input type="file" hidden accept="image/*" onChange={handleFileChange} />
             </Button>
             
             {onSelectFromLibrary && (
                 <Button
                    variant="text"
                    size="small"
                    disabled={disabled}
                    onClick={onSelectFromLibrary}
                 >
                    Select Existing
                 </Button>
             )}
         </Box>
      )}
    </Box>
  );
};

const MissionaryEditor: React.FC<MissionaryEditorProps> = ({ missionaryId, orderId, isLocked, onSave }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [missionary, setMissionary] = useState<Missionary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; msg: string; type: "success" | "error" }>({
    open: false,
    msg: "",
    type: "success",
  });
  
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [missingTextItems, setMissingTextItems] = useState<string[]>([]);
  const [missingPhotoItems, setMissingPhotoItems] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // Media Picker State
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'missionary' | { companionIndex: number } | null>(null);

  // Load Global Missionary Data
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const missionaries = (userDoc.data().missionaries as Missionary[]) || [];
          const found = missionaries.find((m) => m.id === missionaryId);
          if (found) {
            setMissionary(found);
          } else {
            console.error("Missionary not found");
          }
        }
      } catch (err) {
        console.error("Error loading missionary", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user, missionaryId]);

  const handleSave = async (silent = false) => {
    if (!user || !missionary) return;
    if (!silent) setSaving(true);
    try {
      // 1. Get current list
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const currentList = (userDoc.data()?.missionaries as Missionary[]) || [];

      // 2. Update specific missionary
      const updatedList = currentList.map((m) => (m.id === missionaryId ? missionary : m));

      // 3. Write back
      await updateDoc(userRef, { missionaries: updatedList });
      
      if (!silent) {
        setToast({ open: true, msg: "Changes saved successfully", type: "success" });
        if (onSave) onSave();
      }
    } catch (err) {
      console.error("Error saving:", err);
      setToast({ open: true, msg: "Failed to save changes", type: "error" });
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const finishOrderProcess = async () => {
     setValidationDialogOpen(false);
     if (!orderId) return;
     setSaving(true);
     
     try {
       // 1. Save Missionary Data First
       await handleSave(true);
       
       // 2. Update Order
       const orderRef = doc(db, "orders", orderId);
       
       // Update Order with link and form status
       await updateDoc(orderRef, {
          linkedMissionaryId: missionaryId, 
          linkedMissionaryName: missionary?.name || "Missionary",
          "formStatus.Missionary": true,
          lastFormUpdate: serverTimestamp()
       });
       
       // 3. Trigger Progress Check
       await checkAndUpdateOrderProgress(orderId, { Missionary: true });
       
       // 4. Navigate back using window location or similar if navigate isn't passed... 
       navigate(`/account/studio/${orderId}`);
       
     } catch (err) {
        console.error("Error finishing order step", err);
        setToast({ open: true, msg: "Error updating order status", type: "error" });
     } finally {
        setSaving(false);
     }
  };

  const handleFinish = async () => {
     if (!missionary) return;
     setHasAttemptedSubmit(true);
     
     // Validate
     const missingText: string[] = [];
     const missingPhotos: string[] = [];

     // Required Text Fields
     if (!missionary.name) missingText.push("Missionary Name");
     if (!missionary.missionDetails.missionName) missingText.push("Mission Name");
     if (!missionary.missionDetails.president) missingText.push("Mission President & Wife");
     if (!missionary.missionDetails.start) missingText.push("Start Date");
     if (!missionary.missionDetails.end) missingText.push("End Date");

     // Required Photos
     if (!missionary.imageFilePath) {
         missingPhotos.push("Photo for " + (missionary.name || "Missionary"));
     }
     
     missionary.companionList.forEach((comp, idx) => {
         if (!comp.name) {
             missingText.push(`Name for companion ${(idx + 1)}`);
         }
         
         if (!comp.imageFilePath) {
             missingPhotos.push(`Photo for companion ${comp.name || (idx + 1)}`);
         }
     });

     if (missingText.length > 0) {
         setMissingTextItems(missingText);
         setMissingPhotoItems([]); // Clear photos so we only show text error first
         setValidationDialogOpen(true);
     } else if (missingPhotos.length > 0) {
         setMissingTextItems([]);
         setMissingPhotoItems(missingPhotos);
         setValidationDialogOpen(true);
     } else {
         await finishOrderProcess();
     }
  };

  // Helper to add to Media Library
  const addToMediaLibrary = async (file: File, url: string) => {
      if (!user) return;
      
      const fileData = { 
        name: file.name, 
        url, 
        size: file.size, 
        uploadedAt: new Date().toISOString() 
      };

      try {
          // 1. Add to Global Library
          const userDocRef = doc(db, "users", user.uid);
          await setDoc(userDocRef, {
            mediaLibrary: arrayUnion(fileData)
          }, { merge: true });

          // 2. Add to Order if linked
          if (orderId) {
             const orderRef = doc(db, "orders", orderId);
             await updateDoc(orderRef, {
                 uploadedFiles: arrayUnion(fileData)
             });
          }
      } catch (err) {
          console.error("Error adding to media library", err);
      }
  };

  const handleMissionaryPhotoUpload = async (url: string, file: File) => {
      if (!missionary) return;
      
      // Update Local State
      const updatedMissionary = { ...missionary, imageFilePath: url };
      setMissionary(updatedMissionary);
      
      // Auto Save to Persistence
      // We must pass the updated object directly because state update is async
      await saveMissionaryDirectly(updatedMissionary);
      
      // Add to Media Library
      if (url && file.name) {
         await addToMediaLibrary(file, url);
      }
  };

  const handleCompanionPhotoUpload = async (idx: number, url: string, file: File) => {
      if (!missionary) return;
      
      const newCompanions = [...missionary.companionList];
      newCompanions[idx] = { ...newCompanions[idx], imageFilePath: url };
      
      const updatedMissionary = { ...missionary, companionList: newCompanions };
      setMissionary(updatedMissionary);
      
      await saveMissionaryDirectly(updatedMissionary);

      if (url && file.name) {
          await addToMediaLibrary(file, url);
      }
  };

  // Dedicated saver that doesn't rely on state closure
  const saveMissionaryDirectly = async (dataToSave: Missionary) => {
      if (!user) return;
      try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          const currentList = (userDoc.data()?.missionaries as Missionary[]) || [];
          const updatedList = currentList.map((m) => (m.id === missionaryId ? dataToSave : m));
          await updateDoc(userRef, { missionaries: updatedList });
          setToast({ open: true, msg: "Photo saved automatically", type: "success" });
      } catch (err) {
          console.error("Auto-save failed", err);
      }
  };

  const handleDetailsChange = (field: keyof MissionDetails, value: string) => {
    if (!missionary) return;
    setMissionary({
      ...missionary,
      missionDetails: { ...missionary.missionDetails, [field]: value },
    });
  };

  const handleCompanionChange = (index: number, field: keyof CompanionDetails, value: string) => {
    if (!missionary) return;
    const newCompanions = [...missionary.companionList];
    newCompanions[index] = { ...newCompanions[index], [field]: value };
    setMissionary({ ...missionary, companionList: newCompanions });
  };

  const addCompanion = () => {
    if (!missionary) return;
    const newComp: CompanionDetails = {
      id: `comp-${Date.now()}`,
      name: "",
      startDate: "",
      endDate: "",
      imageFilePath: "",
    };
    setMissionary({ ...missionary, companionList: [...missionary.companionList, newComp] });
  };

  const removeCompanion = (index: number) => {
    if (!missionary) return;
    const newCompanions = [...missionary.companionList];
    newCompanions.splice(index, 1);
    setMissionary({ ...missionary, companionList: newCompanions });
  };

  if (loading) return <CircularProgress />;
  if (!missionary) return <Alert severity="error">Missionary data not found.</Alert>;

  return (
    <Box sx={{ maxWidth: "1000px", margin: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif' }}>
          Editing: {missionary.name}
        </Typography>
        
        {!isLocked && (
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={() => handleSave(false)}
                disabled={saving}
                >
                {saving ? "Saving..." : "Save Draft"}
                </Button>
                
                {orderId && (
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={handleFinish}
                        disabled={saving}
                    >
                        Mark as Complete
                    </Button>
                )}
            </Box>
        )}
      </Box>

      {/* Locked Warning */}
      {isLocked && (
          <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="subtitle2" fontWeight="bold">This missionary profile is locked for editing.</Typography>
              Because your order is in progress or completed, changes are restricted to prevent disrupting the design process.
              <br/>
              If you need to request changes, please email us at <b>support@familytreeandme.com</b> (Note: Changes may incur a fee if design work has already started).
          </Alert>
      )}

      {/* Main Details */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          opacity: isLocked ? 0.7 : 1,
          pointerEvents: isLocked ? "none" : "auto",
        }}
      >
        <Typography variant="h6" gutterBottom color="primary">
          Mission Details
        </Typography>
        <Grid container spacing={3}>
          {/* Missionary Photo */}
          <Grid
            size={{ xs: 12, sm: 3 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <FileUploadButton
              label="Missionary's Photo"
              currentUrl={missionary.imageFilePath}
              storagePath={`users/${user?.uid}/missionaries/${missionaryId}`}
              onUpload={handleMissionaryPhotoUpload}
              disabled={isLocked}
              onSelectFromLibrary={() => {
                setPickerTarget("missionary");
                setPickerOpen(true);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 9 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label="Missionary Name"
                  value={missionary.name}
                  disabled={isLocked}
                  error={hasAttemptedSubmit && !missionary.name}
                  helperText={
                    hasAttemptedSubmit && !missionary.name ? "Required" : ""
                  }
                  onChange={(e) =>
                    setMissionary({ ...missionary, name: e.target.value })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label="Mission Name"
                  placeholder="e.g. Brazil Rio de Janeiro"
                  value={missionary.missionDetails.missionName}
                  disabled={isLocked}
                  error={
                    hasAttemptedSubmit && !missionary.missionDetails.missionName
                  }
                  helperText={
                    hasAttemptedSubmit && !missionary.missionDetails.missionName
                      ? "Required"
                      : ""
                  }
                  onChange={(e) =>
                    handleDetailsChange("missionName", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Mission President & Wife"
              placeholder="e.g. President and Sister Smith"
              value={missionary.missionDetails.president}
              disabled={isLocked}
              error={
                hasAttemptedSubmit && !missionary.missionDetails.president
              }
              helperText={
                hasAttemptedSubmit && !missionary.missionDetails.president
                  ? "Required"
                  : ""
              }
              onChange={(e) =>
                handleDetailsChange("president", e.target.value)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              required
              label="Start Date"
              placeholder="YYYY-MM"
              value={missionary.missionDetails.start}
              disabled={isLocked}
              error={hasAttemptedSubmit && !missionary.missionDetails.start}
              helperText={
                hasAttemptedSubmit && !missionary.missionDetails.start
                  ? "Required"
                  : ""
              }
              onChange={(e) => handleDetailsChange("start", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              required
              label="End Date"
              placeholder="YYYY-MM"
              value={missionary.missionDetails.end}
              disabled={isLocked}
              error={hasAttemptedSubmit && !missionary.missionDetails.end}
              helperText={
                hasAttemptedSubmit && !missionary.missionDetails.end
                  ? "Required"
                  : ""
              }
              onChange={(e) => handleDetailsChange("end", e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Companions */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          opacity: isLocked ? 0.7 : 1,
          pointerEvents: isLocked ? "none" : "auto",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" color="primary">
            Companions
          </Typography>
          <Button
            startIcon={<Add />}
            onClick={addCompanion}
            variant="outlined"
            size="small"
            disabled={isLocked}
          >
            Add Companion
          </Button>
        </Box>

        {missionary.companionList.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic">
            No companions added yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {missionary.companionList.map((comp: CompanionDetails, idx: number) => (
              <Grid size={{ xs: 12 }} key={comp.id}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start" }}
                >
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      sx={{ width: "30px", fontWeight: "bold", color: "text.secondary" }}
                    >
                      {idx + 1}.
                    </Typography>
                  </Box>

                  {/* Companion Photo */}
                  <FileUploadButton
                    label="Photo"
                    currentUrl={comp.imageFilePath}
                    storagePath={`users/${user?.uid}/missionaries/${missionaryId}/companions`}
                    onUpload={(url, file) =>
                      handleCompanionPhotoUpload(idx, url, file)
                    }
                    disabled={isLocked}
                    onSelectFromLibrary={() => {
                      setPickerTarget({ companionIndex: idx });
                      setPickerOpen(true);
                    }}
                  />

                  <Box
                    sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <TextField
                      label="Companion Name"
                      size="small"
                      fullWidth
                      required
                      value={comp.name}
                      disabled={isLocked}
                      error={validationDialogOpen && !comp.name}
                      helperText={
                        validationDialogOpen && !comp.name ? "Required" : ""
                      }
                      onChange={(e) =>
                        handleCompanionChange(idx, "name", e.target.value)
                      }
                    />
                    <TextField
                      label="Note / Area"
                      size="small"
                      fullWidth
                      value={comp.notes || ""}
                      disabled={isLocked}
                      onChange={(e) =>
                        handleCompanionChange(idx, "notes", e.target.value)
                      }
                    />
                  </Box>
                  <IconButton
                    color="error"
                    onClick={() => removeCompanion(idx)}
                    disabled={isLocked}
                  >
                    <Delete />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* TODO: Add Photo Upload Section for Missionary and Companions */}
      <Alert severity="info" sx={{ mt: 4 }}>
        Photos are automatically saved when you upload them. Click "Save Draft"
        to ensure all text changes are saved.
      </Alert>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.msg}
      />

      {/* Validation Dialog */}
      <Dialog open={validationDialogOpen} onClose={() => setValidationDialogOpen(false)}>
        {missingTextItems.length > 0 ? (
          // BLOCKING TEXT ERROR
          <>
            <DialogTitle>Required Information Missing</DialogTitle>
            <DialogContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                Please provide the following required information before marking
                as complete:
              </Alert>
              <List dense>
                {missingTextItems.map((item, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={`• ${item}`} />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setValidationDialogOpen(false)}
                variant="contained"
                autoFocus
              >
                OK, I'll Fix It
              </Button>
            </DialogActions>
          </>
        ) : (
          // WARNING PHOTO ERROR
          <>
            <DialogTitle>Missing Photos Detected</DialogTitle>
            <DialogContent>
              <DialogContentText>
                It looks like the following items are missing photos:
              </DialogContentText>
              <List dense>
                {missingPhotoItems.map((item, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={`• ${item}`} />
                  </ListItem>
                ))}
              </List>
              <DialogContentText sx={{ mt: 2 }}>
                We highly recommend uploading these now to avoid delays. Are you
                sure you want to mark this as complete?
              </DialogContentText>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>
                  By proceeding, I acknowledge that generic placeholder images
                  will be used for any missing photos.
                </strong>
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setValidationDialogOpen(false)}>
                Go Back & Upload
              </Button>
              <Button
                onClick={finishOrderProcess}
                color="error"
                variant="contained"
                autoFocus
              >
                Mark Complete Anyway
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Media Picker */}
      <MediaLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => {
          if (pickerTarget === "missionary") {
            handleMissionaryPhotoUpload(url, {
              name: "Library Image",
              size: 0,
            } as File);
          } else if (
            pickerTarget &&
            typeof pickerTarget === "object" &&
            "companionIndex" in pickerTarget
          ) {
            handleCompanionPhotoUpload(pickerTarget.companionIndex, url, {
              name: "Library Image",
              size: 0,
            } as File);
          }
          setPickerOpen(false);
        }}
      />
    </Box>
  );
};

export default MissionaryEditor;

